import React, { memo, useCallback, useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { useNavigate } from "react-router-dom"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import BottomAction from "@fuse/sharedComponents/buttons/BottomAction"
import SolidBtn from "@fuse/sharedComponents/buttons/SolidBtn"
import { EventPluginsRenderer } from "@fuse/sharedComponents/EventPluginsLoader"
import { InstrestedEmailCapture } from "@fuse/sharedComponents/instrestedEmailCapture"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import MapWebView from "@fuse/sharedComponents/map"
import Navbar from "@fuse/sharedComponents/navbar/Navbar"
import ProgressiveImage from "@fuse/sharedComponents/progressiveImage/ProgressiveImage"
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import {
  EButtonType,
  EEventStatus,
  EFeatureFlags,
  EIcons,
  EVENT_CLASS,
  EVENT_STATUS,
  EventDocData,
  EventStatusType,
  TEventDetail,
} from "@letshang/constants"
import { get, isEmpty } from "lodash-es"
import { useRecoilState } from "recoil"
import { ROUTES } from "src/App"

import { EnabledPlugin } from "@fuse/recoil/plugins"
import { UserRole } from "@fuse/recoil/pluginStudio/types"
import { userState } from "@fuse/recoil/signup"

import { functionsAccessor } from "@firebaseConfig/clientApp"

import { useProcessFeatureFlags } from "@settingsAlias/featureFlags"
import momentTz from "@settingsAlias/momentInstance"
import moment from "@settingsAlias/momentInstance"

import { increaseInterestedCountApi } from "@api/firebaseWrappedApis"

import { isDev, safeJsonParse } from "@fuse/utils/common"

import StripeConnectionBanner from "@fuse/components/ui/StripeConnectionBanner"

import AdminDownloadAppModal, {
  AdminDownloadAppModalHandle,
} from "@fuse/pages/eventPage/components/AdminDownloadAppModal"
import BuddiesGuestList from "@fuse/pages/eventPage/components/BuddiesGuestList"
import FeedBackModal from "@fuse/pages/eventPage/components/FeedBackModal"
import { WhichBtnState } from "@fuse/pages/eventPage/controller"
import RenderEventDescription, {
  RenderEventDescriptionViaHtml,
} from "@fuse/pages/eventPage/RenderEventDescription"
import DownloadAppModal, {
  DownloadAppModalHandle,
} from "@fuse/pages/landingPage/components/DownloadAppModal"

import First from "@fuse/assets/images/img-event-first.svg"

import AvatarGroup from "./components/AvatarGroup"
// Import the new component sections
import {
  AdminNotice,
  DraftEventNotice,
  EventActions,
  EventBottomActions,
  EventDescription,
  EventFooterSection,
  EventHeader,
  EventImage,
  EventJoinInfo,
  EventLocation,
  EventMap,
  EventPricing,
  EventSocialSection,
  EventTiming,
  InterestSection,
  NeedHelpSection,
} from "./components/EventSections"
import SettingsBottomSheet, {
  SettingsBottomSheetHandle,
} from "./eventManagement-hackathon-4/SettingsBottomSheet"
import "./style.css"

// Extended EventDocData interface to include plugin-related fields
interface EventDataWithPlugins extends EventDocData {
  coHosts?: Record<string, boolean>
  enabled_plugins?: EnabledPlugin[]
}

const EventDetails = ({
  eventDetails,
  eventDocData,
  hasNotJoined,
  isAdmin,
  myEventActions,
  onLeaveEvent,
  canLeaveEvent,
  isUserEmailExist,
  openEmailVerification,
  eventStatus,
  isDraftEvent,
  acceptedGuestAvatars,
  onEventJoin,
  guestList,
  flyerImage,
  showCached,
  cachedDetails,
  invitedId,
  onEventAction,
  hasEventEnded,
  onOpenSettingsBottomSheet,
  onOpenBuddiesGuestList,
  onFeedbackModalStateChange,
  detailsContainerRef,
  isAnyGuestListWebEnabled,
}: {
  eventDetails?: TEventDetail | null
  eventDocData?: EventDocData
  hasNotJoined?: boolean
  isAdmin?: boolean
  myEventActions?: any[]
  onLeaveEvent?: () => void
  canLeaveEvent?: boolean
  isUserEmailExist?: boolean
  openEmailVerification?: () => void
  myTicketStatus?: string
  eventStatus?: string
  isDraftEvent?: boolean
  acceptedGuestAvatars?: any[]
  onEventJoin?: () => void
  guestList?: any[]
  flyerImage?: string
  showCached?: boolean
  cachedDetails?: any
  invitedId?: string
  onEventAction?: (index: number) => void
  isOpenGuestList?: boolean
  setIsOpenGuestList?: (val: boolean) => void
  hasEventEnded?: boolean
  myJoinStatus?: WhichBtnState | EventStatusType
  onOpenSettingsBottomSheet?: () => void
  onOpenBuddiesGuestList?: () => void
  onFeedbackModalStateChange?: (isOpen: boolean) => void
  detailsContainerRef?: React.RefObject<HTMLDivElement>
  isAnyGuestListWebEnabled?: boolean
}) => {
  // Re-render tracking (simplified)
  const renderCount = useRef(0)
  renderCount.current++

  console.log(`[EventDetails] Render #${renderCount.current}`, {
    timestamp: new Date().toISOString(),
    hasNotJoined,
    isAdmin,
    myEventActions: myEventActions?.length || 0,
    acceptedGuestAvatars: acceptedGuestAvatars?.length || 0,
    guestList: guestList?.length || 0,
    eventStatus,
    isDraftEvent,
  })

  // Extract computed values to stable variables to prevent child re-renders
  const eventDescription = get(eventDocData, "eventDescription", "")
  const eventDescriptionViaHtml = get(
    eventDocData,
    "event_description_via_html",
    "",
  )
  const hasJoined = hasNotJoined !== undefined ? !hasNotJoined : false
  const params = useParams()
  const navigate = useNavigate()

  const eventId = get(params, "eventId", "")
  const displayTimezone = get(
    eventDocData,
    "timing.displayTimezone",
    "America/New_York",
  )

  // Calculate date range display
  const mStart = momentTz(get(eventDocData, "timing.eventDate", new Date())).tz(
    displayTimezone,
  )
  const mEnd = momentTz(
    get(eventDocData, "timing.eventEndDate", new Date()),
  ).tz(displayTimezone)
  const now = momentTz() // for "current-year" comparisons

  let displayDateRange: string
  if (mStart.isSame(mEnd, "day")) {
    displayDateRange = now.isSame(mStart, "year")
      ? mStart.format("ddd, MMM DD")
      : mStart.format("ddd, MMM DD YYYY")
  } else if (mStart.isSame(mEnd, "year")) {
    // multi-day but same year
    if (now.isSame(mStart, "year")) {
      displayDateRange = `${mStart.format(
        "ddd, MMM DD",
      )} - ${mEnd.format("ddd, MMM DD")}`
    } else {
      displayDateRange = `${mStart.format(
        "ddd, MMM DD YYYY",
      )} - ${mEnd.format("ddd, MMM DD YYYY")}`
    }
  } else {
    displayDateRange = `${mStart.format(
      "ddd, MMM DD YYYY",
    )} - ${mEnd.format("ddd, MMM DD YYYY")}`
  }

  const eventStartTime = momentTz(
    get(eventDocData, "timing.eventStartTime", new Date()),
  )
    .tz(displayTimezone)
    .format("LT")

  const eventEndTime = momentTz(
    get(eventDocData, "timing.eventEndTime", new Date()),
  )
    .tz(displayTimezone)
    .format("LT")

  const signUpCloseDate = momentTz(
    get(eventDocData, "timing.signupCloseDate", new Date()),
  )
    .tz(displayTimezone)
    .format("MMM DD YYYY")

  const signUpCloseTime = momentTz(
    get(eventDocData, "timing.signupCloseTime", new Date()),
  )
    .tz(displayTimezone)
    .format("LT")

  const eventLocationString = get(eventDocData, "location.eventLocation", "-")
  const otherLocationDetails = get(
    eventDocData,
    "location.otherLocationDetails",
    "",
  )

  const eventStatusData = get(
    eventDocData,
    "eventStatus",
    EEventStatus.UPCOMING,
  ) as EEventStatus

  const totalTicketQuantity = get(eventDetails, "totalTicketQuantity", 0)
  const totalPurchasedTickets = get(eventDetails, "totalPurchasedTickets", 0)
  const ticketLeftString = `${totalTicketQuantity - totalPurchasedTickets} tickets left`
  const postJoinLocation = `${eventLocationString} ${otherLocationDetails && `| ${otherLocationDetails}`}`
  const minTicketPrice = get(eventDetails, "minTicketPrice", 0)
  const maxTicketPrice = get(eventDetails, "maxTicketPrice", 0)
  const [user, setUser] = useRecoilState(userState)

  const locationInfo =
    hasJoined || isAdmin
      ? postJoinLocation
      : `${eventLocationString.split(",")[0]}... (Join to see full address)`

  const isSingleTicket = minTicketPrice === maxTicketPrice

  const eventClass = get(
    eventDocData,
    "eventClass",
    EVENT_CLASS.NON_TICKETED_EVENT,
  ) as EVENT_CLASS
  const isTicketedEvent = eventClass === EVENT_CLASS.TICKETED_EVENT

  // Check if flexible event creation is enabled
  const isFlexibleEventCreationEnabled = useProcessFeatureFlags(
    EFeatureFlags.FLEXIBLE_EVENT_CREATION,
  )

  // Check if we should show the Stripe connection banner
  const shouldShowStripeBanner =
    isFlexibleEventCreationEnabled &&
    isAdmin &&
    isTicketedEvent &&
    !user?.stripeAccountId

  const eventLocation = get(eventDocData, "location", "-")
  const latitude =
    typeof eventLocation === "string" ? 0 : eventLocation.latitude
  const longitude =
    typeof eventLocation === "string" ? 0 : eventLocation.longitude

  const isLocationHidden =
    (get(eventDocData, "location.guestLocationPreview", "Hidden") ||
      "Hidden") === "Hidden"

  const isBuddiesEnabled =
    useProcessFeatureFlags(EFeatureFlags.BUDDIES_SECTION) || false

  // Internal state
  const [isInterested, setIsInterested] = useState(false)
  const [isInterestedLoading, setIsInterestedLoading] = useState(false)
  const [isShowEmailPopup, setIsShowEmailPopup] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const [pendingHelpMessage, setPendingHelpMessage] = useState("")
  const [isSendingFeedback, setIsSendingFeedback] = useState(false)

  // Stable callback functions to prevent child re-renders
  const openGoogleCalendar = React.useCallback(() => {
    const baseUrl = "https://calendar.google.com/calendar/render"
    const title = get(eventDocData, "eventName", "-")
    const startDate = get(
      eventDocData,
      "timing.eventStartTime",
      new Date().toDateString(),
    )
    const endDate = get(
      eventDocData,
      "timing.eventEndTime",
      new Date().toDateString(),
    )

    const formatDateForCalendar = (dateString: string) => {
      const date = new Date(dateString)
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"
    }

    const formattedStartDate = formatDateForCalendar(startDate)
    const formattedEndDate = formatDateForCalendar(endDate)

    const url = `${baseUrl}?action=TEMPLATE&text=${encodeURIComponent(
      title,
    )}&details=${encodeURIComponent(eventDescription)}&location=${encodeURIComponent(
      eventLocationString,
    )}&dates=${formattedStartDate}/${formattedEndDate}`

    window.open(url)
  }, [eventDocData, eventDescription, eventLocationString])

  const handleInterestedClick = React.useCallback(async () => {
    try {
      setIsInterestedLoading(true)
      await increaseInterestedCountApi({ eventId })
      setIsShowEmailPopup(true)
      setIsInterested(true)
      localStorage.setItem(`interested_${eventId}`, "true")
    } catch (error) {
      console.error("Error marking interest:", error)
    } finally {
      setIsInterestedLoading(false)
    }
  }, [eventId])

  const showEdit = useProcessFeatureFlags(EFeatureFlags.EDIT_EVENT)
  const showNeedHelp = useProcessFeatureFlags(EFeatureFlags.NEED_HELP_SUPPORT)

  const unCachedLocation = cachedDetails?.location
    ? safeJsonParse(cachedDetails?.location)
    : {}

  const navigateToChooseTicket = React.useCallback(() => {
    if (totalTicketQuantity - totalPurchasedTickets <= 0) {
      showInfoToast("No tickets left. Event is sold out!")
      return
    }
    if (!eventDetails?.eventDocData?.showSoldOut && hasNotJoined) {
      navigate(ROUTES.CHOOSE_TICKET.replace(":eventId", eventId), {
        state: { invitedId },
      })
    }
  }, [
    totalTicketQuantity,
    totalPurchasedTickets,
    eventDetails,
    hasNotJoined,
    navigate,
    eventId,
    invitedId,
  ])

  useEffect(() => {
    const isInterested = localStorage.getItem(`interested_${eventId}`)
    setIsInterested(isInterested === "true")
  }, [eventId])

  const modalRef = useRef<DownloadAppModalHandle>(null)
  const adminDownloadModalRef = useRef<AdminDownloadAppModalHandle>(null)

  const handleHelpMessageSubmit = (message: string) => {
    setPendingHelpMessage(message)
  }

  const handleSendFeedback = async () => {
    if (!pendingHelpMessage?.trim()) {
      showErrorToast("Please enter a message first.")
      return
    }

    setIsSendingFeedback(true)
    try {
      const sendFeedback = functionsAccessor.httpsCallable(
        "eventManagement-sendFeedbackForEvent",
      )
      const response = await sendFeedback({
        eventId: params.eventId,
        feedback: pendingHelpMessage,
      })

      const result = response.data as { success: boolean; message: string }
      if (result.success) {
        setPendingHelpMessage("")
        showSuccessToast("Message sent!", {
          text2: "Our team will reach out to you.",
        })
      } else {
        showErrorToast(
          result.message || "Failed to send message. Please try again.",
        )
      }
    } catch (error) {
      console.error("Error sending feedback:", error)
      showErrorToast("Failed to send message. Please try again.")
    } finally {
      setIsSendingFeedback(false)
    }
  }

  const isEventHost = user?.uid === get(eventDocData, "initiatorUid")
  const isCoHost = user?.uid
    ? Object.keys(get(eventDocData, "coHosts", {})).includes(user.uid)
    : false
  const canSeeFeedback = isAdmin || isEventHost || isCoHost
  const isAdminOrHost = isAdmin || isEventHost || isCoHost

  // Function to handle join status changes for plugins
  const handlePluginJoinStatusChange = useCallback((hasJoined: boolean) => {
    // This callback can be used for any additional logic when join status changes
    console.log("[EventDetails] Plugin join status changed:", hasJoined)
  }, [])

  // Enhanced onEventJoin that also updates plugin join status
  const enhancedOnEventJoin = useCallback(() => {
    if (onEventJoin) {
      onEventJoin()
      // Update plugin join status immediately
      setTimeout(() => {
        if ((window as any).updatePluginJoinStatus) {
          ;(window as any).updatePluginJoinStatus(true)
        }
      }, 100) // Small delay to ensure the join process has started
    }
  }, [onEventJoin])

  if (showCached)
    return (
      <BackgroundWrapper
        bgImage={cachedDetails.actualFlyerUrl}
        className={`min-h-[100vh] h-full `}
      >
        <BlurWrapper
          blurKey={get(blurKeys, "mainBlurView")}
          overlayOpacityDefault="75"
          overlayColorDefault="dark"
          className="min-h-[100vh] h-full flex flex-col overflow-auto"
        >
          <div id="upper-body" className="flex-1">
            <Navbar />

            <div id="body">
              <div className="flex justify-center mt-[10px] md:mt-0 lg:mt-[74px]  lg:p-4 md:mb-[74px] mb-[50px] overflow-hidden">
                <div className="flex-1 max-w-screen-xl">
                  <div className="flex rounded-xl md:p-8 lg:p-12 flex-col lg:flex-row flex-wrap ">
                    <div className="w-6/12 mx-auto lg:pr-6 mb-6 lg:mb-0 ">
                      <div className=" px-4  md:max-w-md lg:max-w-lg ">
                        {cachedDetails?.showFlyer ? (
                          <img
                            src={cachedDetails.actualFlyerUrl}
                            className="w-full rounded-2xl"
                            alt="First"
                          />
                        ) : (
                          <ProgressiveImage
                            lowResImage={
                              isDev()
                                ? `http://127.0.0.1:9199/walkme-e7896.appspot.com/pixelated_event_images/${eventId}.png`
                                : `https://firebasestorage.googleapis.com/v0/b/walkme-e7896.appspot.com/o/pixelated_event_images/${eventId}.png?alt=media`
                            }
                            highResImage={cachedDetails.actualFlyerUrl}
                            alt="First"
                          />
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="px-5 pb-5">
                        <div className=" flex gap-4 items-center">
                          <h5 className="text-3xl md:text-4xl text-start text-white ">
                            {cachedDetails?.eventName}
                          </h5>
                        </div>
                        {cachedDetails?.initiatorName && (
                          <div className="flex flex-row mt-2 items-center">
                            <div className="h-6 w-6 rounded-full bg-gray-400 overflow-hidden mr-2 sm:hidden">
                              <img
                                src={get(
                                  cachedDetails,
                                  "initiatorAvatarUrl",
                                  "",
                                )}
                                alt="profile"
                                className="w-full h-full"
                              />
                            </div>
                            <h5 className="text-start text-white/35">
                              by {get(cachedDetails, "initiatorName", "-")}
                            </h5>
                          </div>
                        )}

                        <div className="my-6 bg-white/10 border-white/20 border p-4 rounded-2xl">
                          <div
                            className="flex flex-row mb-6 cursor-pointer"
                            onClick={openGoogleCalendar}
                          >
                            <div className="flex text-center items-center">
                              <p className="text-3xl">🗓️</p>
                            </div>
                            <div className="flex-col ml-3">
                              <h5 className="text-start text-white">
                                {momentTz(cachedDetails.eventDate)
                                  .tz(displayTimezone)
                                  .format("ddd, MMM DD YYYY")}
                              </h5>

                              <h5 className="text-start text-white/35 text-sm font-medium">
                                {momentTz(cachedDetails.eventDate)
                                  .tz(displayTimezone)
                                  .format("LT")}
                              </h5>
                            </div>
                          </div>

                          <div className="flex flex-row mb-6">
                            <div className="flex text-center items-center">
                              <p className="text-2xl">📍</p>
                            </div>

                            <div className="flex-col ml-4">
                              <h5 className="text-start text-white">
                                {unCachedLocation?.guestLocationPreview ||
                                  "Hidden"}
                              </h5>
                              <h5 className="text-start text-white/35 text-sm font-medium">
                                {unCachedLocation?.eventLocation || "-"}
                              </h5>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white/10 border-white/20 border px-4 py-3 rounded-2xl">
                          <h6 className="text-lg sm:text-xl text-start text-white">
                            About this event
                          </h6>
                          {eventDescriptionViaHtml ? (
                            <RenderEventDescriptionViaHtml
                              eventDescriptionViaHtml={eventDescriptionViaHtml}
                            />
                          ) : (
                            cachedDetails.eventDescription && (
                              <h6 className="text-md text-start mt-1 text-white/80">
                                <div>
                                  <RenderEventDescription
                                    eventDescription={
                                      cachedDetails.eventDescription
                                    }
                                  />
                                </div>
                              </h6>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </BlurWrapper>
      </BackgroundWrapper>
    )

  return (
    <>
      <DownloadAppModal ref={modalRef} />
      <AdminDownloadAppModal ref={adminDownloadModalRef} />
      <FeedBackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => {
          setIsFeedbackModalOpen(false)
          onFeedbackModalStateChange?.(false)
        }}
        onMessageSubmit={handleHelpMessageSubmit}
        pendingMessage={pendingHelpMessage}
      />

      {isShowEmailPopup && (
        <InstrestedEmailCapture onClose={() => setIsShowEmailPopup(false)} />
      )}

      {isInterestedLoading && <LoadingSpinner />}

      <div className="flex justify-center mt-[10px] md:mt-0 lg:mt-[74px] lg:p-4 md:mb-[74px] mb-1 sm:mb-[50px] overflow-hidden">
        <div className="flex-1 max-w-screen-xl">
          <div className="flex rounded-xl md:p-8 lg:p-12 flex-col lg:flex-row flex-wrap ">
            <EventImage flyerImage={flyerImage} isGuestView={!isAdminOrHost}>
              {/* Join buttons for guest desktop view */}
              {!isAdminOrHost && !isEmpty(myEventActions) && (
                <BottomAction
                  bottomActionClassName="!flex !flex-col w-full"
                  buttonWrapperClassName="!w-full !flex-none"
                  solidBtnClassName="w-full !flex py-3 rounded-lg"
                  buttonOptionList={myEventActions}
                  handleScreen={onEventAction}
                />
              )}

              {/* Share and More buttons for admin/host desktop view */}
              {isAdminOrHost && (
                <div className="flex gap-2 w-full">
                  {/* More button - 10% width */}
                  {isAdmin &&
                    eventDocData?.eventStatus === EVENT_STATUS.OPEN &&
                    !hasEventEnded &&
                    showEdit && (
                      <div style={{ width: "10%" }}>
                        <SolidBtn
                          size="lg"
                          variant={EButtonType.solid}
                          disabled={false}
                          onClick={onOpenSettingsBottomSheet}
                          className="w-full !flex py-3 rounded-lg text-white opacity-100"
                        >
                          {"..."}
                        </SolidBtn>
                      </div>
                    )}

                  {/* Share button - 90% width */}
                  {!isEmpty(myEventActions) && (
                    <div style={{ width: "90%" }}>
                      <BottomAction
                        bottomActionClassName="!flex !flex-col w-full"
                        buttonWrapperClassName="!w-full !flex-none"
                        solidBtnClassName="w-full !flex py-3 rounded-lg"
                        buttonOptionList={myEventActions}
                        handleScreen={onEventAction}
                      />
                    </div>
                  )}
                </div>
              )}
            </EventImage>

            <div
              ref={detailsContainerRef}
              className="flex-1 lg:max-h-screen lg:overflow-y-auto no-scrollbar"
            >
              <div className="px-5 pb-5">
                <div className="mb-6">
                  <EventHeader
                    eventDocData={eventDocData}
                    hasNotJoined={hasNotJoined}
                    eventStatus={eventStatus}
                    eventStatusData={eventStatusData}
                  />
                </div>

                <div className="mb-6">
                  <AdminNotice
                    isAdmin={isAdmin}
                    onDownloadAppClick={() => modalRef.current?.openModal()}
                    isHidden={shouldShowStripeBanner}
                  />
                </div>

                {shouldShowStripeBanner &&
                  (isSingleTicket ? minTicketPrice > 0 : true) && (
                    <div className="mb-6">
                      <StripeConnectionBanner eventId={eventId} />
                    </div>
                  )}

                <div className="mb-6">
                  <DraftEventNotice
                    isAdmin={isAdmin}
                    isDraftEvent={isDraftEvent}
                  />
                </div>

                <div className="mb-6">
                  <InterestSection
                    isDraftEvent={isDraftEvent}
                    eventDocData={eventDocData}
                    isInterested={isInterested}
                    onInterestedClick={handleInterestedClick}
                  />
                </div>

                <div className="mb-6 bg-white/10 border-white/20 border px-4 py-3 rounded-2xl">
                  <EventTiming
                    displayDateRange={displayDateRange}
                    eventStartTime={eventStartTime}
                    eventEndTime={eventEndTime}
                    onCalendarClick={openGoogleCalendar}
                  />

                  <EventLocation
                    eventDocData={eventDocData}
                    hasJoined={hasJoined}
                    isLocationHidden={isLocationHidden}
                    locationInfo={postJoinLocation}
                    latitude={latitude}
                    longitude={longitude}
                    isAdmin={isAdmin}
                    isTicketedEvent={isTicketedEvent}
                    myTicketStatus={get(eventDetails, "myTicketStatus")}
                  />

                  <EventPricing
                    isTicketedEvent={isTicketedEvent}
                    ticketLeftString={ticketLeftString}
                    minTicketPrice={minTicketPrice}
                    maxTicketPrice={maxTicketPrice}
                    isSingleTicket={isSingleTicket}
                    onTicketClick={navigateToChooseTicket}
                  />

                  <EventJoinInfo
                    isTicketedEvent={isTicketedEvent}
                    eventDocData={eventDocData}
                    signUpCloseDate={signUpCloseDate}
                    signUpCloseTime={signUpCloseTime}
                  />

                  <EventActions
                    canLeaveEvent={canLeaveEvent}
                    onLeaveEvent={onLeaveEvent}
                  />
                </div>

                <div className="mb-6">
                  <EventSocialSection
                    isBuddiesEnabled={isBuddiesEnabled}
                    acceptedGuestAvatars={acceptedGuestAvatars}
                    isAdmin={isAdmin}
                    eventDetails={eventDetails}
                    eventDocData={eventDocData}
                    onGuestListClick={() => {
                      if (isAnyGuestListWebEnabled && isAdmin) {
                        navigate(
                          ROUTES.GUEST_LIST.replace(":eventId", eventId),
                          {
                            state: { eventDetails },
                          },
                        )
                      } else {
                        onOpenBuddiesGuestList()
                      }
                    }}
                    onAdminDownloadClick={() =>
                      adminDownloadModalRef.current?.openModal()
                    }
                    isGuestView={!isAdminOrHost}
                  />
                </div>

                <div className="mb-6">
                  <EventDescription
                    eventDescription={eventDescription}
                    eventDescriptionViaHtml={eventDescriptionViaHtml}
                    isAdmin={isAdminOrHost}
                  />
                </div>

                <div className="mb-6">
                  <EventPluginsRenderer
                    eventDocData={eventDocData}
                    eventId={eventId}
                    user={user}
                    isAdmin={isAdmin}
                    unCachedLocation={unCachedLocation}
                    showUserViewConfig={true}
                    isEventDetailsPage={true}
                    hasJoined={!hasNotJoined}
                    onJoinStatusChange={handlePluginJoinStatusChange}
                    pluginContainerClassName="!border-white/20"
                  />
                </div>

                <div className="mb-6">
                  <EventMap
                    hasJoined={hasJoined}
                    eventDocData={eventDocData}
                    isAdmin={isAdminOrHost}
                  />
                </div>

                {/* Need help section */}
                {canSeeFeedback && showNeedHelp && (
                  <NeedHelpSection
                    pendingHelpMessage={pendingHelpMessage}
                    isSendingFeedback={isSendingFeedback}
                    onMessageClick={() => {
                      setIsFeedbackModalOpen(true)
                      onFeedbackModalStateChange?.(true)
                    }}
                    onSendFeedback={handleSendFeedback}
                  />
                )}

                {/* Footer section */}
                <div className="mb-6">
                  <EventFooterSection
                    isGuestView={!isAdminOrHost}
                    isAdmin={isAdmin}
                    fromScreen="event-details"
                    userId={user?.uid}
                  />
                </div>

                {/* Note: Admin/host buttons are now positioned below the flyer image */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// Remove the complex memo comparison logic since we now have fine-grained components
export default memo(EventDetails)
